import { Close as CloseIcon } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import classnames from 'classnames';
import { Base64 } from 'js-base64';
import {
  fetchTransformDoc,
  getCodeContainer,
  getReusableTransformListing,
  setListingTransformType,
} from 'js/actions';
import { parsePackages } from 'new/components/flows/types/byo/core/model';
import MarkdownEditor from 'new/components/LexicalMarkdownEditor';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import Constants from 'js/Constants';
import { GET_CODE_CONTAINERS_REQUEST, GET_TRANSFORM_DOC_REQUEST } from 'js/constants/actionTypes';
import { SearchFilterViews } from 'js/constants/visibilityFilters';
import DateUtils from 'js/lib/DateUtils';
import { map, pick } from 'lodash';
import { ReactComponent as DocumentIcon } from 'new/components/icons/fi-rr-document.svg';
import { ReactComponent as SearchIcon } from 'new/components/icons/search.svg';
import { TransformType } from 'new/components/ReusableTransform/types';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCodeContainersById } from 'new/state/entities/selectors/transformSelectors';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import classes from './LoadCustomProcessor.module.scss';
import SearchControls from './SearchControls';
import { Stack } from 'new/elements/Stack';

interface LoadCustomProcessorProps {
  sourceConfig: any;
  onSourceConfigChanged: (config: any) => void;
  inline?: boolean;
  /**
   * When provided, each list item also gets a "Load as Template" button next
   * to "Show Code" that decodes the picked processor's code/packages and
   * hands them here — e.g. so a caller can copy them into its own editable
   * "Provide Your Own Function" draft as a starting point, distinct from
   * picking the processor by reference (which is what onSourceConfigChanged
   * already does). Omitted entirely (no button rendered) for callers that
   * only need reference-picking, e.g. the Drawer-mode DirectFlow usage.
   */
  onLoadAsTemplate?: (code: string, packages: string[]) => void;
}

const LoadCustomProcessor = ({
  sourceConfig,
  onSourceConfigChanged,
  inline = false,
  onLoadAsTemplate,
}: LoadCustomProcessorProps) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const isLoading = useIsLoading(GET_CODE_CONTAINERS_REQUEST);
  const transformIds = useSelector((state: any) => state.transforms.listing);
  const transforms = useSelector(selectCodeContainersById);
  // The listing only filters by output_type='custom' (see setListingTransformType
  // below), which RAG ai_function containers also satisfy — filter to real
  // source_custom containers here so a RAG function can't be picked as a source's
  // existing code (the backend rejects a source_custom's code_container_id
  // pointing at a non-source container). Confirmed 2026-08-19.
  const items = pick(
    transforms,
    transformIds.filter((id) => transforms[id]?.resource_type === 'source_custom'),
  );
  const { customProcessorId } = sourceConfig;
  const transform = transforms[customProcessorId];

  // Root cause, confirmed live 2026-08-21 (network trace): this MUST run
  // before SearchControls' own mount effect calls loadListData — that
  // thunk reads `state.transforms.listingTransformType` synchronously at
  // dispatch time. `SearchControls` is a CHILD of this component, and
  // React fires child `useEffect`s before parent `useEffect`s on mount, so
  // a plain `useEffect` here loses the race: the picker's very first
  // search fired with whatever type was left over from wherever this view
  // was last used (e.g. ATTRIBUTE), not CUSTOM — wrong/empty results that
  // only self-corrected once something else (the access-level toggle)
  // triggered a second search after this effect had caught up.
  // `useLayoutEffect` is NOT ordering-sensitive the same way: React flushes
  // every layout effect in the whole commit, parent AND child, before ANY
  // passive `useEffect` runs — so switching this one hook fixes the race
  // without touching SearchControls (shared with legacy DirectFlow/Reusable
  // Transforms) at all.
  useLayoutEffect(() => {
    dispatch(setListingTransformType(TransformType.CUSTOM));

    return () => {
      dispatch(setListingTransformType(TransformType.ATTRIBUTE));
    };
  }, []);

  const onOpen = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const onSelectionChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedId(parseInt(event.target.value) || null);
  };

  const onConfirm = () => {
    onSourceConfigChanged({
      ...sourceConfig,
      customProcessorId: selectedId,
    });
    onClose();
  };

  const chooseButton = (
    <Button
      variant="outlined"
      color="primary"
      disabled={!selectedId}
      onClick={onConfirm}
      data-testid="select-connector-processor-button"
    >
      Choose
    </Button>
  );

  // The processor list + search + footer — shared by the Drawer and inline modes.
  const pickerBody = (
    <>
      <header>
        <Typography variant="subheading">Select a Connector Processor</Typography>
        {!inline && (
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </header>
      <div className={classes.searchControlsContainer}>
        <SearchControls
          view={SearchFilterViews.REUSABLE_TRANSFORMS}
          isLoading={isLoading}
          loadData={getReusableTransformListing}
          actionSlot={chooseButton}
        />
      </div>
      <RadioGroup className={classes.list} value={selectedId} onChange={onSelectionChanged}>
        {map(items, (item) => (
          <ListItem
            key={item.id}
            data={item}
            isSelected={item.id === selectedId}
            onLoadAsTemplate={onLoadAsTemplate}
          />
        ))}
      </RadioGroup>
      {!inline && (
        <footer>
          <Button variant="outlined" color="basic" onClick={onClose}>
            Cancel
          </Button>
        </footer>
      )}
    </>
  );

  // Inline mode: render the picker table directly in the page (no button/drawer).
  if (inline) {
    return (
      <div className={classes.root}>
        {transform && (
          <div className={classes.header}>
            <div className={classes.headerLeft}>
              <span className={classes.name} data-testid="selected-custom-processor-name">
                {transform.name}
              </span>
              <ChipWithCopy
                toCopy={String(transform.id)}
                data-testid="selected-custom-processor-id"
              >
                ID {transform.id}
              </ChipWithCopy>
              <span className={classes.timestamp}>
                Last Modified: {DateUtils.convertDateStringToFormat(transform.updated_at)}
              </span>
            </div>
          </div>
        )}
        <div className={classes.inlinePicker}>{pickerBody}</div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        {transform && (
          <div className={classes.headerLeft}>
            <span className={classes.name} data-testid="selected-custom-processor-name">
              {transform.name}
            </span>
            <ChipWithCopy toCopy={String(transform.id)} data-testid="selected-custom-processor-id">
              ID {transform.id}
            </ChipWithCopy>
            <span className={classes.timestamp}>
              Last Modified: {DateUtils.convertDateStringToFormat(transform.updated_at)}
            </span>
          </div>
        )}
        <Button
          className={classes.loadButton}
          variant="outlined"
          color="primary"
          onClick={onOpen}
          data-testid="load-custom-processor-button"
        >
          <SearchIcon /> Find an existing Connector Processor
        </Button>
      </div>
      <TabContent transformId={customProcessorId} />
      <Drawer className={classes.drawer} open={isOpen}>
        {pickerBody}
      </Drawer>
    </div>
  );
};

const TabContent = ({ transformId }) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const transforms = useSelector(selectCodeContainersById);
  const transform = transforms[transformId];

  useEffect(() => {
    if (transformId && !transform) {
      dispatch(getCodeContainer(transformId));
    }
  }, [transformId]);

  if (!transformId) {
    return (
      <div className={classes.emptyMessage}>
        Load an existing processor or write a new one to continue.
      </div>
    );
  }

  if (!transform) {
    return <CircularProgress size={46} />;
  }

  return (
    <div className={classes.tabContent}>
      <div className={classes.tabContentSection}>
        <TransformCode data={transform} />
      </div>
      <div className={classes.tabContentSection}>
        <TransformDoc data={transform} />
      </div>
    </div>
  );
};

const ListItem = ({
  data,
  isSelected,
  onLoadAsTemplate,
}: {
  data: any;
  isSelected: boolean;
  onLoadAsTemplate?: (code: string, packages: string[]) => void;
}) => {
  const [mode, setMode] = useState<'code' | 'doc' | 'none'>('none');
  const { id, name, description } = data;
  const showCode = mode === 'code';
  const showDoc = mode === 'doc';

  const onShowCode = (event) => {
    event.stopPropagation();
    setMode(showCode ? 'none' : 'code');
  };

  const onShowDoc = (event) => {
    event.stopPropagation();
    setMode(showDoc ? 'none' : 'doc');
  };

  const onLoadTemplate = (event) => {
    event.stopPropagation();
    const { code, code_encoding, code_config } = data;
    let decodedCode: string;
    try {
      decodedCode = code_encoding === 'base64' ? Base64.decode(code) : JSON.stringify(code);
    } catch (error) {
      decodedCode = '';
    }
    onLoadAsTemplate?.(decodedCode, parsePackages(code_config?.packages ?? ''));
  };

  return (
    <div
      key={id}
      className={classnames(classes.listItem, 'ellipsis', {
        [classes.selected]: isSelected,
      })}
      data-testid="connector-processor-list-item"
    >
      <label className={classes.itemContent} htmlFor={`transform-choice-${id}`}>
        <Radio id={`transform-choice-${id}`} value={id} color="primary" />
        <Stack flex={1}>
          <Typography className={`${classes.infoContainer} ellipsis`}>{name}</Typography>
          {description && <div className={`${classes.description} ellipsis`}>{description}</div>}
        </Stack>
        <ChipWithCopy toCopy={id}>ID {id}</ChipWithCopy>
        <Button
          className={classes.listItemButton}
          variant="outlined"
          color="primary"
          onClick={onShowCode}
          data-testid="show-connector-processor-code"
        >
          Show Code
        </Button>
        {onLoadAsTemplate && (
          <Button
            className={classes.listItemButton}
            variant="outlined"
            color="primary"
            onClick={onLoadTemplate}
            data-testid="load-connector-processor-as-template"
          >
            Load as Template
          </Button>
        )}
        <IconButton className={classes.documentIconButton} color="primary" onClick={onShowDoc}>
          <DocumentIcon className={classes.documentIcon} />
        </IconButton>
      </label>
      <Collapse in={showDoc || showCode}>
        <div className={classes.listItemExpandedContainer}>
          {showCode && <TransformCode data={data} />}
          {showDoc && <TransformDoc data={data} />}
        </div>
      </Collapse>
    </div>
  );
};

const TransformCode = ({ data }) => {
  const { code, code_encoding } = data;
  let decodedCode: string;

  try {
    decodedCode = code_encoding === 'base64' ? Base64.decode(code) : JSON.stringify(code);
  } catch (error) {
    decodedCode = '';
  }

  return (
    <span data-testid="connector-processor-code">
      <LoadableMonacoEditor
        language={Constants.CodeLanguages.PYTHON}
        theme="dark"
        height="100%"
        options={{
          readOnly: true,
          minimap: { enabled: false },
        }}
        value={decodedCode}
      />
    </span>
  );
};

const TransformDoc = ({ data }) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const docById = useSelector((state: any) => state.transforms.docById);
  const docs = useSelector((state: any) => state.entities.docs);
  const isLoadingDoc = useIsLoading(GET_TRANSFORM_DOC_REQUEST);
  const { id } = data;
  const docContent = docs[docById[id]]?.text || '';

  useEffect(() => {
    dispatch(fetchTransformDoc(id));
  }, []);

  return isLoadingDoc && !docContent ? (
    <div className={classes.loaderContainer}>
      <CircularProgress size={46} />
    </div>
  ) : (
    <div className={classes.docContainer} data-testid="connector-processor-doc">
      <MarkdownEditor
        committedValue={{ value: docContent }}
        placeholder="No documentation found."
        readOnly
      />
    </div>
  );
};

export default LoadCustomProcessor;
